import { Controller, useForm } from "react-hook-form";
import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { Button2 } from "@/ui2/atoms/Button";
import { captureErrorMessage, parseError } from "@/lib/helpers/captureErrorMessage";
import CrossIcon from "@/assets/images/icons2/icon--close.svg?component";
import { Label2 } from "@/ui2/atoms/Label";
import { ModalControlled2, ModalDivider } from "@/ui2/molecules/Modal";
import { showToast } from "@/providers/ToastProvider";
import { TextArea2 } from "@/ui2/atoms/TextArea";
import { useAcuityResolution } from "@/features/acuity/api/mutations/useAcuityResolution";
import type { UUID } from "@/core/types";

import { acuityKeys } from "../../(header-acuity)/api/keyFactory";

type RejectFormValues = {
  rejection_reason: string;
};

const DEFAULT_VALUES: RejectFormValues = { rejection_reason: "" };

const MAX_CHAR_LENGTH = 1_000;

export function RejectAcuityModal({ entity_id, patientId }: { entity_id: UUID; patientId: UUID }) {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { control, formState, handleSubmit, reset } = useForm<RejectFormValues>({
    defaultValues: DEFAULT_VALUES,
    mode: "onSubmit",
  });

  useEffect(() => {
    if (!isModalOpen) reset(DEFAULT_VALUES);
  }, [isModalOpen, reset]);

  const { isPending, mutateAsync: submitResolution } = useAcuityResolution({
    onError: (error) =>
      captureErrorMessage("Failed to reject acuity override", {
        tags: { error: parseError({ error }) },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: acuityKeys.history(patientId),
      });
      setIsModalOpen(false);
      showToast([{ heading: "Acuity override rejected", variant: "success" }]);
    },
  });

  const onSubmit = useCallback(
    ({ rejection_reason }: RejectFormValues) =>
      submitResolution({ data: { entity_id, rejection_reason, status: "REJECTED" }, patientId }),
    [submitResolution, entity_id, patientId],
  );

  const isCancelDisabled = isPending || formState.isSubmitting;
  const isSubmitDisabled = isPending || formState.isSubmitting || !formState.isValid;

  return (
    <>
      <Button2
        icon={<CrossIcon />}
        onPress={() => setIsModalOpen(true)}
        size="sm"
        variant="destructive-primary"
      >
        Reject
      </Button2>

      <ModalControlled2
        isOpen={isModalOpen}
        onOpenChange={setIsModalOpen}
        title="Reject Acuity Override"
      >
        <form className="tw:flex tw:flex-col tw:gap-4" onSubmit={handleSubmit(onSubmit)}>
          <p className="body-md">
            This request will be rejected. The patient&apos;s current acuity level will remain
            unchanged.
          </p>

          <div className="tw:flex tw:flex-col tw:gap-1.5">
            <Label2 htmlFor="rejection-reason" label="Rejection reason" />

            <Controller
              control={control}
              name="rejection_reason"
              render={({ field }) => (
                <TextArea2
                  id="rejection-reason"
                  maxLength={MAX_CHAR_LENGTH}
                  onChange={field.onChange}
                  placeholder="Please provide additional details"
                  rows={5}
                  value={field.value}
                />
              )}
              rules={{ maxLength: MAX_CHAR_LENGTH, required: true }}
            />
          </div>

          <ModalDivider />

          <div className="tw:flex tw:justify-end tw:gap-3">
            <Button2
              isDisabled={isCancelDisabled}
              onPress={() => setIsModalOpen(false)}
              variant="secondary"
            >
              Cancel
            </Button2>

            <Button2
              icon={<CrossIcon />}
              isDisabled={isSubmitDisabled}
              isLoading={isPending}
              type="submit"
              variant="destructive-secondary"
            >
              Reject Override
            </Button2>
          </div>
        </form>
      </ModalControlled2>
    </>
  );
}
